import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Star, ShoppingCart, Eye, Ban } from 'lucide-react';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useCart } from '../../contexts/CartContext';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

export function ProductCard({ product, className, isPreviewMode = false }) {
  const { t, i18n } = useTranslation();
  const { formatPrice } = useCurrency();
  const { addToCart } = useCart();
  const [searchParams] = useSearchParams();
  
  // Detect preview mode from URL or prop
  const previewToken = searchParams.get('token');
  const inPreview = isPreviewMode || !!previewToken;

  // Extraire les données selon la langue actuelle
  const name = product.name?.[i18n.language] || product.name?.fr || 'Produit';
  
  // Support both image formats: array or object with main/gallery
  const image = Array.isArray(product.images) 
    ? (product.images[0] || '/placeholder-product.jpg')
    : (product.images?.main || '/placeholder-product.jpg');
  
  const rating = product.stats?.average_rating || product.stats?.rating_avg || 4.5;
  const reviewCount = product.stats?.reviews_count || 0;
  const soldCount = product.stats?.purchases_count || product.stats?.sales_count || 0;
  const badges = product.badges || {};
  
  // Déterminer le badge principal à afficher
  const getBadge = () => {
    if (badges.is_new) return { text: t('badges.new'), color: 'bg-green-500' };
    if (badges.is_trending) return { text: t('badges.trending'), color: 'bg-orange-500' };
    if (badges.is_almost_sold_out) return { text: t('badges.almostSoldOut'), color: 'bg-mutagim-red' };
    if (badges.is_mutagim_pick) return { text: t('badges.mutagimPick'), color: 'bg-purple-500' };
    if (badges.is_premium) return { text: t('badges.premium'), color: 'bg-yellow-500' };
    return null;
  };
  
  const badge = getBadge();

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (inPreview) {
      toast.info('Mode Preview — Action désactivée');
      return;
    }
    
    await addToCart(product.id);
  };

  return (
    <Link 
      to={`/product/${product.slug}`}
      className={cn(
        "group relative flex flex-col bg-gray-900 rounded-lg overflow-hidden",
        "border border-gray-800 hover:border-mutagim-red/50 transition-all duration-300",
        "hover:shadow-lg hover:shadow-mutagim-red/10 hover:-translate-y-1",
        className
      )}
      data-testid={`product-card-${product.slug}`}
    >
      {/* Image container */}
      <div className="relative aspect-square overflow-hidden bg-gray-800">
        <img
          src={image}
          alt={name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        
        {/* Badge */}
        {badge && (
          <span 
            className={cn(
              "absolute top-2 start-2 px-2 py-1 text-xs font-bold text-white rounded",
              badge.color
            )}
            data-testid="product-badge"
          >
            {badge.text}
          </span>
        )}
        
        {/* Quick actions overlay */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
          {inPreview ? (
            <div className="flex flex-col items-center gap-2 text-white/80">
              <Ban className="w-6 h-6" />
              <span className="text-xs">Mode Preview</span>
            </div>
          ) : (
            <>
              <Button
                size="icon"
                variant="secondary"
                className="bg-white/90 hover:bg-white text-mutagim-dark"
                onClick={handleAddToCart}
                data-testid="quick-add-cart"
              >
                <ShoppingCart className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="secondary"
                className="bg-white/90 hover:bg-white text-mutagim-dark"
                data-testid="quick-view"
              >
                <Eye className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-4 space-y-2">
        {/* Product name */}
        <h3 className="text-white font-medium text-sm line-clamp-2 group-hover:text-mutagim-red transition-colors">
          {name}
        </h3>
        
        {/* Rating */}
        <div className="flex items-center gap-1">
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={cn(
                  "h-3 w-3",
                  star <= Math.floor(rating) 
                    ? "fill-yellow-400 text-yellow-400" 
                    : "text-gray-600"
                )}
              />
            ))}
          </div>
          <span className="text-xs text-gray-400">
            ({reviewCount})
          </span>
        </div>

        {/* Social proof */}
        {soldCount > 0 && (
          <p className="text-xs text-gray-500" data-testid="sold-count">
            {t('product.soldThisMonth', { count: soldCount })}
          </p>
        )}

        {/* Price */}
        <div className="mt-auto pt-2 flex items-baseline gap-2">
          <span className="text-lg font-bold text-mutagim-red" data-testid="product-price">
            {formatPrice(product.pricing?.prices)}
          </span>
          {product.pricing?.compare_at_prices && (
            <span className="text-sm text-gray-500 line-through">
              {formatPrice(product.pricing.compare_at_prices)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default ProductCard;
